import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { REASONS, SUPPORT_TIMES, INTERESTS } from '../components/OnboardingFlow';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * PersonalizePage — "Personalize My Experience" (Prompt #6 §25).
 * Lets a logged-in user edit their onboarding preferences at any time.
 * Changes hit `POST /api/onboarding` (upsert) and immediately affect
 * subsequent /api/today recommendations. Preserves listening history
 * and never resets streaks.
 */
const Chip = ({ label, active, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] transition-all border ${
      active
        ? 'bg-[#173F35] text-[#F6F1E7] border-[#173F35] shadow-sm'
        : 'bg-[#FFFCF6] text-stone-700 border-stone-200 hover:border-[#C79A4A]/50'
    }`}
  >
    {active && <Check className="w-3.5 h-3.5" />}
    {label}
  </button>
);

const PersonalizePage = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [times, setTimes] = useState([]);
  const [interests, setInterests] = useState([]);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('rbr_token');
    if (!token) return;
    fetch(`${API_URL}/api/onboarding`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const prefs = d?.prefs || {};
        setReasons(prefs.reasons || []);
        setTimes(prefs.supportTimes || []);
        setInterests(prefs.interests || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  if (isLoading) return <div className="p-8 text-center text-stone-500">Loading…</div>;
  if (!user) { navigate('/auth'); return null; }

  const toggle = (list, setter) => (id) => setter(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('rbr_token');
      const r = await fetch(`${API_URL}/api/onboarding`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reasons, supportTimes: times, interests, completed: true }),
      });
      if (!r.ok) throw new Error('save failed');
      toast.success('Preferences saved');
    } catch {
      toast.error('Could not save preferences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pb-32 min-h-screen bg-[#F6F1E7]" data-testid="personalize-page">
      <div className="px-5 pt-5 pb-3">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-[12px] text-stone-500 hover:text-stone-700 mb-3"
          data-testid="personalize-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <p className="rbr-eyebrow mb-2" style={{ color: '#C79A4A' }}>Personalize</p>
        <h1
          className="text-3xl leading-tight text-stone-800 mb-1"
          style={{ fontFamily: "'DM Serif Display', 'Playfair Display', serif" }}
        >
          Personalize my experience.
        </h1>
        <p className="text-sm text-stone-500">
          Tell Rowdy what you'd like more of. Update anytime — your streaks, journal, and progress stay right where they are.
        </p>
      </div>

      {loading ? (
        <p className="text-center py-10 text-stone-500">Loading your preferences…</p>
      ) : (
        <div className="px-5 space-y-6">
          <section>
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-[#C79A4A]" />
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#C79A4A]">What I want support with</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {REASONS.map((r) => (
                <Chip
                  key={r.id}
                  label={r.label}
                  active={reasons.includes(r.id)}
                  onClick={() => toggle(reasons, setReasons)(r.id)}
                  testid={`personalize-reason-${r.id}`}
                />
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-[#C79A4A]" />
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#C79A4A]">When I usually need support</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {SUPPORT_TIMES.map((r) => (
                <Chip
                  key={r.id}
                  label={r.label}
                  active={times.includes(r.id)}
                  onClick={() => toggle(times, setTimes)(r.id)}
                  testid={`personalize-time-${r.id}`}
                />
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-[#C79A4A]" />
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#C79A4A]">Content I prefer</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map((r) => (
                <Chip
                  key={r.id}
                  label={r.label}
                  active={interests.includes(r.id)}
                  onClick={() => toggle(interests, setInterests)(r.id)}
                  testid={`personalize-interest-${r.id}`}
                />
              ))}
            </div>
          </section>

          <button
            onClick={save}
            disabled={saving}
            data-testid="personalize-save"
            className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#173F35] text-[#FFFCF6] text-sm font-semibold hover:bg-[#0D2B24] disabled:opacity-60 active:scale-95 transition-all"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Save preferences
          </button>
          <p className="text-[11px] text-stone-400 text-center">
            Your preferences are private — only Rowdy uses them to shape what you see on Today.
          </p>
        </div>
      )}
    </div>
  );
};

export default PersonalizePage;
